<div class="page-container" *ngIf="config">
    <div class="header-section" *ngIf="config.breadcrumbs || config.pageTitle">
        <lib-breadcrumb *ngIf="config.breadcrumbs" [list]="config.breadcrumbs"></lib-breadcrumb>
        <h2 class="page-title" *ngIf="config.pageTitle">{{ config.pageTitle }}</h2>
    </div>

    <!-- Search Criteria Panel -->
    <div class="content-wrapper">
        <form [formGroup]="searchForm" (ngSubmit)="handleSearch()" class="search-form-card">
            <div class="search-grid">
                <div class="form-item" *ngFor="let field of config.searchFields">
                    <mat-label><span *ngIf="field.required" style="color: #f44336; margin-right: 2px;">*</span>{{
                        field.label }}</mat-label>
                    <mat-form-field appearance="outline" class="full-width">
                        <input matInput *ngIf="field.type === 'text'" [formControlName]="field.key"
                            [attr.maxlength]="field.maxLength || null" (keypress)="allowOnlyValidChars($event)"/>
                        <mat-error *ngIf="searchForm.get(field.key)?.hasError('specialChars')">Special characters not
                            allowed.</mat-error>
                        <input matInput type="number"
                                *ngIf="field.type === 'number'"
                                class="no-spinner"
                                [formControlName]="field.key" />

                        <mat-select *ngIf="field.type === 'select'" [formControlName]="field.key">
                            <mat-option *ngFor="let opt of field.options" [value]="opt.value">{{ opt.label
                                }}</mat-option>
                        </mat-select>

                        <ng-container *ngIf="field.type === 'date'">
                            <input matInput [matDatepicker]="picker" [formControlName]="field.key">
                            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                            <mat-datepicker #picker></mat-datepicker>
                        </ng-container>

                        <ng-container *ngIf="field.type === 'datetime'">
                            <div style="display: flex; gap: 8px;">
                                <mat-form-field appearance="outline" style="flex: 1;">
                                    <input matInput [matDatepicker]="dtPickerSearch"
                                        [formControlName]="field.key + '_date'" placeholder="Date">
                                    <mat-datepicker-toggle matSuffix [for]="dtPickerSearch"></mat-datepicker-toggle>
                                    <mat-datepicker #dtPickerSearch></mat-datepicker>
                                </mat-form-field>

                                <mat-form-field appearance="outline" style="flex: 1;">
                                    <mat-select [formControlName]="field.key + '_time'" placeholder="Time">
                                        <mat-option *ngFor="let t of timeOptions" [value]="t">{{ t }}</mat-option>
                                    </mat-select>
                                </mat-form-field>
                            </div>
                        </ng-container>
                    </mat-form-field>
                </div>
                <div class="form-actions">
                    <button type="reset" mat-stroked-button color="primary" [disabled]="isLoading"
                        (click)="handleReset()">Reset</button>
                    <button type="submit" mat-flat-button color="primary"
                        [disabled]="searchForm.invalid || isLoading">Search</button>

                </div>
            </div>
        </form>

        <!-- Table Results Panel -->

        <div class="section-container">
            <div class="section-header">
                <h3 class="heading">{{ config.tablePanelTitle || 'Search Results' }}</h3>
                <div class="action-toolbar">
                    <button mat-button color="primary" *ngIf="config.actions.showAdd"
                        [disabled]="isAddMode || isEditMode || isLoading"
                        (click)="handleAdd()"><mat-icon>add</mat-icon>
                        Add</button>
                    <button mat-button color="primary" *ngIf="config.actions.showSave"
                        [disabled]="(!isAddMode && !isEditMode) || isLoading"
                        (click)="handleSave()"><mat-icon>save</mat-icon>
                        Save</button>
                    <button mat-button color="primary" *ngIf="config.actions.showModify"
                        [disabled]="selectedRows.length === 0 || isAddMode || isEditMode || isLoading || !hasSearched"
                        (click)="handleModify()"><mat-icon>edit</mat-icon> Modify</button>
                    <button mat-button color="warn" *ngIf="config.actions.showDelete"
                        [disabled]="selectedRows.length === 0 || isAddMode || isEditMode || isLoading || !hasSearched"
                        (click)="handleDelete()"><mat-icon>delete</mat-icon> Delete</button>
                    <button mat-button color="warn" *ngIf="isAddMode || isEditMode" [disabled]="isLoading"
                        (click)="handleCancel()"><mat-icon>cancel</mat-icon> Cancel</button>
                </div>
            </div>
            <mat-progress-bar *ngIf="isLoading" mode="indeterminate"
                style="position: absolute; top: 0; left: 0; right: 0; z-index: 20;"></mat-progress-bar>
            <div class="responsive-table-wrapper">
                <table mat-table [dataSource]="dataSource" matSort class="mat-elevation-z0">

                    <!-- Selection Column -->
                    <ng-container matColumnDef="select">
                        <th mat-header-cell *matHeaderCellDef class="w-10"
                            [attr.rowspan]="hasGroups ? 2 : null"
                            mat-sort-header disabled>
                        </th>
                        <td mat-cell *matCellDef="let element">
                            <mat-checkbox [checked]="isSelected(element)" [disabled]="isAddMode || isEditMode"
                                (change)="toggleSelection(element, $event.checked)">
                            </mat-checkbox>
                        </td>
                    </ng-container>

                    <!-- Dynamic Columns -->
                    <ng-container *ngFor="let col of flatColumns" [matColumnDef]="col.key">
                        <th mat-header-cell *matHeaderCellDef
                            mat-sort-header
                            [attr.rowspan]="(hasGroups && !subLevelHeaderColumns.includes(col.key)) ? 2 : null"
                            [style.border-bottom]="(hasGroups && !subLevelHeaderColumns.includes(col.key)) ? '1px solid currentColor' : ''">
                            <span *ngIf="col.required" style="color:red">*</span> {{ col.label }}
                        </th>

                        <td mat-cell *matCellDef="let element">
                            <ng-container *ngIf="!(element._isEdit && col.isEditable)">
                                <!-- Display Mode -->
                                <span *ngIf="col.type === 'date'">
                                    {{ element[col.key] | date:'dd-MMM-yyyy' }}
                                </span>

                                <span *ngIf="col.type !== 'date' && col.type !== 'checkbox'">
                                    {{ element[col.key] }}
                                </span>
                                <mat-checkbox *ngIf="col.type === 'checkbox'" [checked]="element[col.key]"
                                    disabled></mat-checkbox>
                            </ng-container>

                            <ng-container *ngIf="element._isEdit && col.isEditable">
                                <!-- Edit Mode -->
                                <mat-form-field *ngIf="col.type === 'text'" appearance="outline"
                                    class="dense-form-field" style="margin-bottom: -1em;">
                                    <input matInput [(ngModel)]="element[col.key]"
                                        [attr.maxlength]="col.maxLength || null" (keypress)="allowOnlyValidChars($event)">
                                </mat-form-field>

                                <mat-form-field *ngIf="col.type === 'number'" appearance="outline"
                                    class="dense-form-field" style="margin-bottom: -1em;">
                                    <input matInput type="number"  class="no-spinner" [(ngModel)]="element[col.key]">
                                </mat-form-field>

                                <mat-form-field *ngIf="col.type === 'select'" appearance="outline"
                                    class="dense-form-field" style="margin-bottom: -1em;">
                                    <mat-select [(ngModel)]="element[col.key]" panelClass="auto-width-panel"
                                        (selectionChange)="onTableSelectChange(col, $event.value, element)">
                                        <mat-select-trigger>
                                            {{ element[col.key] }}
                                        </mat-select-trigger>
                                        <mat-option *ngFor="let opt of col.options" [value]="opt.value">{{ opt.label
                                            }}</mat-option>
                                    </mat-select>
                                </mat-form-field>

                                <mat-form-field *ngIf="col.type === 'date'" appearance="outline"
                                    class="dense-form-field" style="margin-bottom: -1em;">
                                    <input matInput [matDatepicker]="picker" [(ngModel)]="element[col.key]">
                                    <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                                    <mat-datepicker #picker></mat-datepicker>
                                </mat-form-field>

                                <ng-container *ngIf="col.type === 'datetime'" >
                                <div style="display: flex; gap: 4px; align-items: center;">
                                    <mat-form-field appearance="outline" class="dense-form-field">
                                    <input matInput [matDatepicker]="picker" 
                                            [(ngModel)]="element['_' + col.key + '_date']"
                                            (dateChange)="element['_' + col.key + '_time'] = ''"> <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                                    <mat-datepicker #picker></mat-datepicker>
                                    </mat-form-field>

                                    <mat-form-field appearance="outline" style="width: 100px;" class="dense-form-field">
                                    <mat-select [(ngModel)]="element['_' + col.key + '_time']" 
                                                [disabled]="!element['_' + col.key + '_date']"
                                                placeholder="Time">
                                        <mat-option *ngFor="let t of timeOptions" [value]="t">{{ t }}</mat-option>
                                    </mat-select>
                                    </mat-form-field>
                                </div>
                                </ng-container>
                                <mat-checkbox *ngIf="col.type === 'checkbox'"
                                    [(ngModel)]="element[col.key]"></mat-checkbox>
                            </ng-container>
                        </td>
                    </ng-container>

                    <!-- Group Headers -->
                    <ng-container *ngFor="let group of headerGroups" [matColumnDef]="group.key">
                        <th mat-header-cell *matHeaderCellDef [attr.colspan]="group.colspan" class="group-header-cell"
                            style="text-align: center; border-bottom: none;">
                            {{ group.name }}
                        </th>
                    </ng-container>

                    <!-- Header rows, handle grouped configuration -->
                    <tr mat-header-row *matHeaderRowDef="topLevelHeaderColumns; sticky: true"></tr>
                    <tr mat-header-row *matHeaderRowDef="subLevelHeaderColumns; sticky: true"
                        [style.display]="hasGroups ? '' : 'none'"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
                </table>
            </div>


        </div>
    </div>